"use client";

import React, { useState, useRef, useMemo, useEffect } from "react";
import Image from "next/image";
import { Section, Typography, Card } from "@/components/ui";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import { cn } from "@/lib/utils";

import "swiper/css";

export interface IntegrationItem {
  icon: string;
  alt: string;
  title: string;
}

interface InteractiveAppShowcaseProps {
  title?: string;
  description?: React.ReactNode;
  // Optional, no longer used for layout – kept for backward compatibility
  showcaseImage?: string;
  showcaseImageAlt?: string;
  integrations: IntegrationItem[];
  imgBaseUrl?: string;
  background?: "white" | "gray" | "red" | "gradient-red" | "gradient-light" | "transparent";
  className?: string;
}

export function InteractiveAppShowcase({
  title = "Screens that speak your culture.",
  description = "From lobbies to meeting rooms, Pickcel keeps your workplace informed and energised — through visual, timely communication.",
  showcaseImage,
  showcaseImageAlt = "Digital signage app showcase",
  integrations,
  imgBaseUrl = "",
  background = "transparent",
  className = "",
}: InteractiveAppShowcaseProps) {
  const [activeIndex, setActiveIndex] = useState(0); // selected (tab/card highlight)
  const [displayedIndex, setDisplayedIndex] = useState(0); // actually rendered in the big preview
  const [prevDisplayedIndex, setPrevDisplayedIndex] = useState(0);
  const [isImageTransitioning, setIsImageTransitioning] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const swiperRef = useRef<any>(null);
  const activeIndexRef = useRef(0);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // Map integrations to include screenContent
  const integrationsWithContent = useMemo(() => {
    // Mapping of integration titles to preview images (single image per app)
    const previewImages: Record<string, string> = {
      YouTube: "backgorund-youtube.U9Ru2bGs4RXu.webp",
      URL: "backgorund-url.0QAApc28jI2F.webp",
      Weather: "background-wheather.jTbD5vAH1i4u.webp",
      Scroller: "background-scroller.DPuFtMQEbmHv.webp",
      Dashboard: "background-dashabord.QSMV6y1vBl7e.webp",
      NDTV: "backgorund-ndtv.MUt6YFX0sPyW.webp",
      "Power BI": "backgorund-powerbi.M07dJh24ba1c.webp",
      Clock: "backgorund-clock.CZBxr9brDNqi.webp",
      "Google Slides": "backgorund-goolgle-slide.Ohi6u39v3z-W.webp",
    };

    return integrations.map((item) => ({
      ...item,
      screenContent: {
        src: `${imgBaseUrl}/${previewImages[item.title] || "youtube-preview.nKs9N7rTICQL.webp"}`,
        alt: `${item.title} digital signage screen`,
      },
    }));
  }, [integrations, imgBaseUrl]);

  const handleSlideChange = (swiper: any) => {
    const nextIndex = swiper.realIndex;
    if (nextIndex === activeIndexRef.current) return;
    void selectIndex(nextIndex, swiper.previousRealIndex ?? activeIndexRef.current);
  };

  const selectIndex = (nextIndex: number, previousIndex?: number) => {
    setHasUserInteracted(true);
    setActiveIndex(nextIndex);
    if (nextIndex === displayedIndex) return;

    // Swap in the next image underneath while the previous one stays visible;
    // the preview <Image onLoad> below clears the flag once it has decoded, so
    // there is no black flash and no separate preload fetch.
    setPrevDisplayedIndex(previousIndex ?? displayedIndex);
    setDisplayedIndex(nextIndex);
    setIsImageTransitioning(true);
  };

  const handleIconClick = (index: number) => {
    if (index === activeIndexRef.current) return;
    void selectIndex(index, activeIndexRef.current);
    if (swiperRef.current) {
      // Provide a speed to avoid the default "snap" feel.
      swiperRef.current.slideToLoop(index, 450);
      swiperRef.current.autoplay?.stop?.();
      // In loop mode, let onSlideChange drive activeIndex via swiper.realIndex
    } else {
      // For desktop static row
      // state already set above
    }
  };

  // Autoplay for desktop (static row) - only on desktop screens
  useEffect(() => {
    // Check if we're on desktop (lg breakpoint is 1024px)
    const isDesktop = () => window.innerWidth >= 1024;

    if (!isDesktop()) return; // Don't run on mobile/tablet (Swiper handles it)
    if (hasUserInteracted) return; // Stop auto-advance after user click

    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => {
        return (prevIndex + 1) % integrations.length;
      });
    }, 6000); // 3 seconds delay, same as mobile

    return () => clearInterval(interval);
  }, [integrations.length, hasUserInteracted]);

  const currentScreenContent =
    integrationsWithContent[displayedIndex]?.screenContent ||
    integrationsWithContent[0]?.screenContent;
  const prevScreenContent =
    integrationsWithContent[prevDisplayedIndex]?.screenContent ||
    integrationsWithContent[0]?.screenContent;

  return (
    <Section variant="default" background={background} className={className}>
      <div className="max-w-7xl container mx-auto">
        {/* Title and Description */}
        <div className="text-center justify-center items-center flex-col mb-8 md:mb-12">
          <span className="section-label">Integrations</span>
          <Typography variant="h2" as="h2" className="mb-6">
            {title}
          </Typography>
          <Typography variant="body" as="p" className="text-text-primary max-w-4xl mx-auto">
            {description}
          </Typography>
        </div>

        {/* Integration Icons - Swiper on mobile/tablet, static row on desktop */}
        <div className="mb-8 md:mb-12 px-4 md:px-0">
          <div className="max-w-[1200px] h-[100px] mt-8 mx-auto flex items-center overflow-visible">
            {/* Mobile + Tablet: Swiper */}
            <div className="w-full lg:hidden mt-12">
              <Swiper
                modules={[Autoplay, Navigation]}
                spaceBetween={12}
                slidesPerView={2}
                speed={450}
                breakpoints={{
                  375: {
                    slidesPerView: 3,
                    spaceBetween: 12,
                  },
                  640: {
                    slidesPerView: 4,
                    spaceBetween: 24,
                  },
                  768: {
                    slidesPerView: 6,
                    spaceBetween: 24,
                  },
                }}
                autoplay={{
                  delay: 6000,
                  disableOnInteraction: true,
                }}
                loop={true}
                onSlideChange={handleSlideChange}
                onTouchStart={() => {
                  setHasUserInteracted(true);
                  swiperRef.current?.autoplay?.stop?.();
                }}
                onSliderFirstMove={() => {
                  setHasUserInteracted(true);
                  swiperRef.current?.autoplay?.stop?.();
                }}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                className="w-full py-4 h-[180px] [&_.swiper-button-next]:hidden [&_.swiper-button-prev]:hidden [&_.swiper-pagination]:hidden [&_.swiper-wrapper]:flex [&_.swiper-wrapper]:items-center [&_.swiper-slide]:flex [&_.swiper-slide]:items-center [&_.swiper-slide]:justify-center [&_.swiper-slide]:h-auto"
              >
                {integrationsWithContent.map((item, index) => (
                  <SwiperSlide key={index}>
                    <Card
                      onClick={() => handleIconClick(index)}
                      className={cn(
                        "rounded-xl w-20 h-20 gap-2 flex mt-6 flex-col items-center justify-center cursor-pointer bg-white shadow-none transition-[transform,box-shadow,border-color] duration-200 ease-out will-change-transform transform-gpu hover:scale-[1.04]",
                        activeIndex === index && "shadow-lg border-accent hover:border-accent-hover bg-white"
                      )}
                    >
                      <div className="shrink-0 flex items-center justify-center h-6 w-6">
                        <Image src={item.icon} alt={item.alt} title={item.alt} width={24} height={24} className="object-contain" />
                      </div>
                      <Typography variant="small" as="h3" className="text-center text-xs font-normal leading-tight">
                        {item.title}
                      </Typography>
                    </Card>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Desktop: Static row (no swiper) */}
            <div className="hidden lg:flex w-full justify-center gap-3">
              {integrationsWithContent.map((item, index) => (
                <Card
                  key={index}
                  onClick={() => handleIconClick(index)}
                  className={cn(
                    "rounded-xl w-28 h-28 px-4 py-2 gap-2 flex mt-8 flex-col items-center justify-center cursor-pointer bg-white shadow-none transition-[transform,box-shadow,border-color] duration-200 ease-out will-change-transform transform-gpu hover:scale-[1.04]",
                    activeIndex === index && "shadow-lg border-accent hover:border-accent-hover bg-white"
                  )}
                >
                  <div className="shrink-0 flex items-center justify-center">
                    <Image src={item.icon} alt={item.alt} title={item.alt} width={40} height={40} className="object-contain" />
                  </div>
                  <Typography variant="small" as="h3" className="text-center font-normal leading-tight">
                    {item.title}
                  </Typography>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Single full-width showcase image (per-app) */}
        <div className="relative w-full overflow-hidden rounded-sm md:rounded-lg mt-3 aspect-video bg-white">
          <Image
            src={prevScreenContent.src}
            alt={prevScreenContent.alt || showcaseImageAlt}
            title={prevScreenContent.alt || showcaseImageAlt}
            className={cn(
              "absolute inset-0 rounded-lg object-cover transition-opacity duration-200 ease-out",
              isImageTransitioning ? "opacity-100" : "opacity-0"
            )}
            fill
            sizes="(min-width: 1024px) 1200px, 100vw"
            aria-hidden
            loading="lazy"
            // The source previews on the CDN are 9984x4304, which exceeds the
            // 9000px cap of the Amplify image optimizer: /_next/image answers 400
            // and the preview renders blank. Serve them straight from the CDN.
            unoptimized
          />
          <Image
            src={currentScreenContent.src}
            alt={currentScreenContent.alt || showcaseImageAlt}
            title={currentScreenContent.alt || showcaseImageAlt}
            className={cn(
              "rounded-lg object-cover transition-opacity duration-200 ease-out",
              isImageTransitioning ? "opacity-0" : "opacity-100"
            )}
            fill
            sizes="(min-width: 1024px) 1200px, 100vw"
            loading="lazy"
            unoptimized
            onLoad={() => setIsImageTransitioning(false)}
          />
        </div>
      </div>
    </Section>
  );
}

